/**
 * Tab "Invoice Perlu Dilengkapi" — antrean kelengkapan identitas invoice.
 * Filter: tanpa nomor, tanpa jatuh tempo, sumber belum tertaut, perlu
 * verifikasi, penanggung jawab, proyek, client, umur data. Hanya pengguna
 * dengan perm `collection.complete_identity` yang melihat tombol "Lengkapi".
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Filter, X, Info } from "lucide-react";
import { CompletenessBadges } from "@/components/collection/InvoiceIdentityCell";
import CompleteIdentityDialog from "@/components/collection/CompleteIdentityDialog";

const CARD = "border border-slate-200 bg-white";

function StatCard({ label, value, tone = "slate", active, onClick, testid, tooltip }) {
  const tones = {
    slate: "border-slate-300 text-slate-700",
    rose: "border-rose-300 text-rose-700",
    amber: "border-amber-300 text-amber-800",
    sky: "border-sky-300 text-sky-700",
  };
  return (
    <button type="button" data-testid={testid}
      onClick={onClick}
      title={tooltip}
      className={`border px-3 py-2 text-left transition-colors ${tones[tone]} ${active ? "ring-2 ring-[#FF5A1F]" : "hover:bg-slate-50"}`}>
      <div className="text-[9px] uppercase tracking-wider font-semibold opacity-80 flex items-center gap-1">{label}{tooltip && <Info size={9} className="opacity-60" />}</div>
      <div className="text-xl font-bold tabular-nums leading-tight">{value}</div>
    </button>
  );
}

export default function IncompleteInvoicesTab({ canComplete, onRowChanged }) {
  const [data, setData] = useState({ items: [], stats: null, as_of: null });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    missing_invoice_no: false,
    missing_due: false,
    missing_source: false,
    needs_verification: false,
    pic_id: "",
    project_id: "",
    client: "",
    min_age_days: "",
  });
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v === "" || v === false || v == null) return;
        params.set(k, v === true ? "true" : String(v));
      });
      const { data } = await api.get(`/collection/incomplete?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error("Gagal memuat antrean kelengkapan");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => setFilters({ missing_invoice_no: false, missing_due: false, missing_source: false, needs_verification: false, pic_id: "", project_id: "", client: "", min_age_days: "" });
  const activeFilterCount = useMemo(() => Object.values(filters).filter((v) => v !== "" && v !== false && v != null).length, [filters]);

  const s = data.stats || {};
  const items = data.items || [];

  const inpCls = "w-full border border-slate-300 px-2 h-8 text-xs bg-white focus:outline-none focus:border-[#FF5A1F]";

  return (
    <div className="space-y-4" data-testid="incomplete-invoices-tab">
      {/* Statistik ringkas (dapat dijadikan filter aktif). Tooltip menjelaskan
          cakupan berbeda dengan Ringkasan A.R. (user request E — 2026-10-20). */}
      <div className="border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-900 flex items-start gap-2" data-testid="incomplete-scope-notice">
        <Info size={12} className="shrink-0 mt-0.5" />
        <span>
          <b>Cakupan antrean kelengkapan:</b> seluruh entri Collection — termasuk invoice <i>lunas</i>, <i>draft/perlu verifikasi</i>, dan <i>klaim retensi</i>.
          Berbeda dari <b>Ringkasan A.R.</b> (hanya invoice resmi terbuka — <i>tidak</i> termasuk draft & klaim retensi).
          Rumus piutang tetap sama; angka berbeda karena cakupan berbeda, bukan kesalahan hitung.
          Kartu <i>Perlu Verifikasi</i> di sini menghitung entri berstatus draft <i>termasuk klaim retensi & lunas</i> yang identitasnya belum lengkap; antrean FAT pada sub-tab <b>Invoice Draft / Perlu Verifikasi</b> hanya memuat invoice draft aktif non-klaim (seluruhnya, lengkap maupun belum).
        </span>
      </div>
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <StatCard label="Total Belum Lengkap" value={s.total_incomplete ?? 0} tone="slate" testid="stat-total-incomplete"
            tooltip="Jumlah seluruh entri Collection yang masih kurang nomor invoice, jatuh tempo, tautan sumber, atau butuh verifikasi — meliputi lunas, draft, dan klaim retensi." />
          <StatCard label="Tanpa Nomor Invoice" value={s.missing_invoice_number ?? 0} tone="rose"
            active={filters.missing_invoice_no} onClick={() => setFilters({ ...filters, missing_invoice_no: !filters.missing_invoice_no })}
            testid="stat-missing-invoice-no"
            tooltip="Entri Collection yang nomor invoice-nya belum diisi atau hanya spasi. Nomor tidak dibangkitkan otomatis — WAJIB diisi manual." />
          <StatCard label="Jatuh Tempo Belum Diisi" value={s.missing_due_date ?? 0} tone="amber"
            active={filters.missing_due} onClick={() => setFilters({ ...filters, missing_due: !filters.missing_due })}
            testid="stat-missing-due"
            tooltip="Kualitas data — mencakup lunas/draft/klaim. Entri tanpa jatuh tempo TIDAK dihitung sebagai piutang terlambat (hari keterlambatan = 0). Angka ini berbeda dari 'Invoice resmi terbuka tanpa jatuh tempo' di Ringkasan A.R." />
          <StatCard label="Sumber Belum Tertaut" value={s.missing_source ?? 0} tone="sky"
            active={filters.missing_source} onClick={() => setFilters({ ...filters, missing_source: !filters.missing_source })}
            testid="stat-missing-source"
            tooltip="Entri tanpa deep-link transaksi sumber yang valid. Klik untuk melihat daftar — lengkapi via tombol 'Lengkapi Data'. Tautan tidak dibangkitkan otomatis bila ID sumber belum diisi." />
          <StatCard label="Perlu Verifikasi" value={s.needs_verification ?? 0} tone="amber"
            active={filters.needs_verification} onClick={() => setFilters({ ...filters, needs_verification: !filters.needs_verification })}
            testid="stat-needs-verification"
            tooltip="Draft / Perlu Verifikasi — TIDAK dicampur ke piutang resmi. Isi 'Alasan Perlu Verifikasi' lewat form Lengkapi Data." />
        </div>
      )}

      {/* Filter bar */}
      <div className={`${CARD} px-3 py-2 flex items-center flex-wrap gap-2`} data-testid="incomplete-filter-bar">
        <Filter size={12} className="text-slate-400" />
        <input placeholder="Client" value={filters.client}
          onChange={(e) => setFilters({ ...filters, client: e.target.value })}
          className={`${inpCls} w-32`} data-testid="filter-client" />
        <input placeholder="Proyek ID" value={filters.project_id}
          onChange={(e) => setFilters({ ...filters, project_id: e.target.value })}
          className={`${inpCls} w-36`} data-testid="filter-project" />
        <input placeholder="PIC ID" value={filters.pic_id}
          onChange={(e) => setFilters({ ...filters, pic_id: e.target.value })}
          className={`${inpCls} w-32`} data-testid="filter-pic" />
        <input type="number" placeholder="Umur min (hari)" value={filters.min_age_days}
          onChange={(e) => setFilters({ ...filters, min_age_days: e.target.value })}
          className={`${inpCls} w-32`} data-testid="filter-age" />
        {activeFilterCount > 0 && (
          <Button variant="outline" size="sm" onClick={clearFilters} className="h-7 text-xs" data-testid="btn-clear-filters">
            <X size={11} className="mr-1" /> Bersihkan ({activeFilterCount})
          </Button>
        )}
      </div>

      {!canComplete && (
        <div className="border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900" data-testid="no-perm-notice">
          Anda melihat status kelengkapan namun belum punya perm <b>Collection → Lengkapi Identitas Invoice</b>. Hubungi admin untuk mendapatkan akses perubahan.
        </div>
      )}

      {/* Tabel antrean */}
      <div className={`${CARD} overflow-x-auto`}>
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Proyek / WO", "Client", "Nomor Invoice", "Referensi", "Tgl Invoice", "Jatuh Tempo", "Belum Lengkap", "Verifikasi", "PIC", "Umur", "Aksi"].map((h) => (
                <th key={h} className="px-2 py-2 text-left overline text-slate-600 text-[10px] font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="incomplete-table-body">
            {loading && <tr><td colSpan={11} className="px-4 py-6 text-center text-slate-400 italic">Memuat…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-6 text-center text-slate-400" data-testid="incomplete-empty">
                Tidak ada invoice yang perlu dilengkapi untuk filter ini.
              </td></tr>
            )}
            {!loading && items.map((r) => {
              const ident = r.identity || {};
              const missing = (ident.completeness?.missing_fields || []).map((m) => ({
                invoice_number: "Nomor", invoice_date: "Tgl Invoice", due_date: "Jatuh Tempo", source_link: "Sumber",
              }[m] || m));
              return (
                <tr key={r.entry_key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`incomplete-row-${r.entry_id}`}>
                  <td className="px-2 py-2 font-semibold text-[#0B1D3A]">{ident.project_name || "—"}</td>
                  <td className="px-2 py-2 text-slate-700">{ident.client || "—"}</td>
                  <td className="px-2 py-2 font-mono">{ident.invoice_number || <span className="italic text-rose-600">Belum diisi</span>}</td>
                  <td className="px-2 py-2 font-mono text-slate-600">{ident.external_reference || <span className="italic text-slate-400">—</span>}</td>
                  <td className="px-2 py-2 tabular-nums">{ident.invoice_date ? fmtDate(ident.invoice_date) : <span className="italic text-slate-400">—</span>}</td>
                  <td className="px-2 py-2 tabular-nums">{ident.due_date ? fmtDate(ident.due_date) : <span className="italic text-amber-700">Belum diisi</span>}</td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1 flex-wrap">
                      {missing.length > 0 ? missing.map((m) => (
                        <span key={m} className="px-1 py-0.5 text-[9px] border border-rose-300 bg-rose-50 text-rose-700">{m}</span>
                      )) : <span className="text-slate-300">—</span>}
                    </div>
                  </td>
                  <td className="px-2 py-2"><CompletenessBadges identity={ident} size="xs" /></td>
                  <td className="px-2 py-2 text-slate-700">{ident.pic_name || <span className="italic text-slate-400">—</span>}</td>
                  <td className="px-2 py-2 tabular-nums text-slate-600">{ident.age_days != null ? `${ident.age_days}h` : "—"}</td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {canComplete ? (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]"
                        data-testid={`btn-complete-${r.entry_id}`}
                        onClick={() => setEditRow(r)}>Lengkapi Data</Button>
                    ) : (
                      <span className="text-[10px] text-slate-400 italic">Lihat saja</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CompleteIdentityDialog open={!!editRow} row={editRow}
        onClose={() => setEditRow(null)}
        onSaved={(newRow) => { setEditRow(null); onRowChanged?.(newRow); load(); }} />
    </div>
  );
}
